import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useFormikContext, getIn } from "formik";
import { Link } from "react-router-dom";

import { useInitialDocument } from "hooks/documents";

import CityAutoCompleteSelect from "components/InputsField/formikInputs/CityAutoCompleteSelect";
import FormikTextField from "components/InputsField/formikInputs/FormikTextField";
import FormikDropzoneField from "components/InputsField/formikInputs/FormikDropzoneField";
import AsyncSelect from "components/InputsField/formikInputs/AsyncSelect";
import Title from "components/common/Layout/Title";
import FormRow, { ALIGN } from "components/common/Layout/FormRow";
import { UTILS, PROFILE } from "helpers/routes";
import { MODE_TRANSPORT } from "constants/transport";
import { TransportService } from "service/TransportService";
import ExchangeTransport, { initializeTransportFields } from "models/exchangeTransport";

import styles from "./styles.module.scss";

const NoOptionsMessage = () => {
    const { t } = useTranslation();
    return (
        <div className={styles.noOptions}>
            <span className={styles.noOptions__text}>{t("transportFields.main_section.no_transport")}</span>
            <Link to={PROFILE.addTransport} className={styles.noOptions__link}>
                {t("transportFields.main_section.create_transport")}
            </Link>
        </div>
    );
};

const GeneralInformation = ({
    mode = MODE_TRANSPORT.transport,
    setFieldValue: setFormValues = null,
    /** Узкая форма (модалка): поля идут построчно по два, а не четырьмя колонками */
    stacked = false,
}) => {
    const { t } = useTranslation();
    const [transportOptions, setTransportOptions] = useState([]);
    const currentLang = useSelector((state) => state.settings.lang);
    const companyId = useSelector((state) => state.profile.profileData.company_id);
    const { values } = useFormikContext();
    const { fetchDocumnets } = useInitialDocument();

    const isExchangeMode = mode === MODE_TRANSPORT.exchange;
    const fieldValue = getIn(values, "transport_ids");

    useEffect(() => {
        if (isExchangeMode) {
            TransportService.getTransportTrackpark({ lang: currentLang, companyId })
                .then((res) => {
                    const transportsFormated = res.data.items.map((item) => ({
                        value: item.id,
                        label: `${item.registration_number}${
                            item.own_name ? ` - ${item.own_name}` : ""
                        }`,
                    }));
                    setTransportOptions(transportsFormated);
                })
                .catch((e) => {
                    console.log(e);
                });
        }
    }, [currentLang, companyId, isExchangeMode]);

    const updateFieldValues = useCallback(
        (transport) => {
            const fieldValues = ExchangeTransport.initFormData(transport);
            fieldValues["transport_ids"] = fieldValue;
            setFormValues(fieldValues);
        },
        [fieldValue, setFormValues],
    );

    const handleClear = useCallback(() => {
        setFormValues({
            ...initializeTransportFields,
            transport_ids: "",
        });
    }, [setFormValues]);

    useEffect(() => {
        if (isExchangeMode && fieldValue && setFormValues) {
            TransportService.getTrackParkTransportData(fieldValue, currentLang).then(
                async ({ data }) => {
                    if (data.transport_technical_documents) {
                        try {
                            data.transport_technical_documents = await fetchDocumnets(
                                data.transport_technical_documents,
                            );
                        } catch (e) {}
                    }
                    updateFieldValues(data);
                },
            );
        }
    }, [currentLang, fetchDocumnets, fieldValue, isExchangeMode, setFormValues, updateFieldValues]);

    // Пары полей описаны один раз — раскладка меняется только контейнером
    const registrationFields = [
        <CityAutoCompleteSelect
            key="country"
            noOptionsMessage={t("forms.country.no_option")}
            name="country_id"
            required
            label={t("forms.country.label")}
            labelOutside
            url={UTILS.countries}
        />,
        <FormikTextField
            key="registration_number"
            label={t("transportFields.main_section.license")}
            name="registration_number"
            maxLength="13"
            placeholder="A 007AA 183"
            required={mode === MODE_TRANSPORT.transport}
        />,
    ];

    const descriptionFields = [
        <FormikTextField
            key="own_name"
            label={t("transportFields.main_section.name")}
            name="own_name"
            maxLength="20"
            placeholder={t("transportFields.main_section.name_grey")}
        />,
        <div key="certificate">
            <FormikDropzoneField
                name="transport_technical_documents"
                filesCount="2"
                label={t("transportFields.main_section.certificate")}
            />
        </div>,
    ];

    return (
        <>
            <Title>{t("transportFields.main_section.heading")}</Title>
            {isExchangeMode && setFormValues && (
                <FormRow withMargin>
                    <AsyncSelect
                        options={transportOptions}
                        name="transport_ids"
                        label={t("transportFields.main_section.select_from_fleet")}
                        isClearable
                        onClear={handleClear}
                        noOptionsMessage={() => <NoOptionsMessage />}
                    />
                    <></>
                </FormRow>
            )}
            {stacked ? (
                <>
                    <FormRow withMargin withSpace>
                        {registrationFields}
                    </FormRow>
                    <FormRow withMargin withSpace align={ALIGN.top}>
                        {descriptionFields}
                    </FormRow>
                </>
            ) : (
                <FormRow withMargin align={ALIGN.top}>
                    <FormRow withSpace>{registrationFields}</FormRow>
                    <FormRow withSpace align={ALIGN.top}>
                        {descriptionFields}
                    </FormRow>
                </FormRow>
            )}
        </>
    );
};

export default GeneralInformation;
